/**
 * Google Analytics 4 - Utility pro měření a tracking
 * 
 * Zajišťuje:
 * - Bezpečné načtení GA4 skriptu
 * - Error handling při blokování adblockerem
 * - Debug mód s konzole logováním
 * - GDPR compliant tracking
 */

// Globální proměnná pro sledování stavu GA
let gaInitialized = false;
let debugMode = false;

/**
 * Kontrola, zda je GA4 načteno a dostupné
 * @returns {boolean}
 */
export function isGALoaded() {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function' && 
         gaInitialized;
}

/**
 * Logování v debug módu
 * @param {string} action - Název akce
 * @param {object} data - Data k logování
 */
function debugLog(action, data) {
  if (debugMode) {
    console.log('[GA4]', action, data);
  }
}

/**
 * Inicializace Google Analytics 4
 * 
 * @param {string} measurementId - GA4 Measurement ID (G-XXXXXXXXXX)
 * @param {boolean} debug - Zapnutí debug módu
 * @returns {Promise<boolean>} - True pokud se inicializace podařila
 */
export function initGA(measurementId, debug = false) {
  return new Promise((resolve) => {
    try {
      // Kontrola, zda jsme v prohlížeči
      if (typeof window === 'undefined') {
        console.warn('[GA4] Nelze inicializovat - nejsme v prohlížeči');
        resolve(false);
        return;
      }

      // Kontrola, zda již není inicializováno
      if (gaInitialized) {
        debugLog('initGA', 'GA4 již je inicializováno');
        resolve(true);
        return;
      }

      // Kontrola validity measurement ID
      if (!measurementId || !measurementId.startsWith('G-')) {
        console.error('[GA4] Neplatné measurement ID:', measurementId);
        resolve(false);
        return;
      }

      debugMode = debug;
      debugLog('initGA', { measurementId, debugMode });

      // Inicializace dataLayer
      window.dataLayer = window.dataLayer || [];
      
      // Vytvoření gtag funkce
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      // Nastavení času
      window.gtag('js', new Date());

      // Konfigurace GA4 s debug módem
      const config = {
        send_page_view: false, // Budeme posílat manuálně
      };

      if (debugMode) {
        config.debug_mode = true;
      }

      window.gtag('config', measurementId, config);

      // Dynamické načtení GA4 skriptu
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      
      // Handler pro úspěšné načtení
      script.onload = () => {
        gaInitialized = true;
        debugLog('initGA', 'Skript úspěšně načten');
        resolve(true);
      };

      // Handler pro selhání načtení (např. adblock)
      script.onerror = () => {
        console.warn('[GA4] Nepodařilo se načíst skript (možná je blokován adblockerem)');
        gaInitialized = false;
        resolve(false);
      };

      // Přidání skriptu do DOM
      document.head.appendChild(script);

    } catch (error) {
      console.error('[GA4] Chyba při inicializaci:', error);
      gaInitialized = false;
      resolve(false);
    }
  });
}

/**
 * Sledování zobrazení stránky
 * 
 * @param {string} path - Cesta stránky (např. /dashboard)
 * @param {string} title - Název stránky (volitelné)
 */
export function trackPageView(path, title = '') {
  try {
    if (!isGALoaded()) {
      debugLog('trackPageView', 'GA4 není načteno, přeskakuji');
      return;
    }

    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    
    const params = {
      page_path: path,
    };

    if (title) {
      params.page_title = title;
    }

    debugLog('trackPageView', params);

    if (typeof window.gtag === 'function') {
      window.gtag('config', measurementId, params);
    }

  } catch (error) {
    console.error('[GA4] Chyba při trackování page view:', error);
  }
}

/**
 * Sledování custom události
 * 
 * @param {string} eventName - Název události (např. 'start_challenge')
 * @param {object} params - Parametry události (nesmí obsahovat osobní údaje!)
 * 
 * Příklady použití:
 * trackEvent('start_challenge', { challenge_type: 'denni_vyzva' })
 * trackEvent('complete_test', { test_type: 'diagnosticky', score: 85 })
 */
export function trackEvent(eventName, params = {}) {
  try {
    if (!isGALoaded()) {
      debugLog('trackEvent', `GA4 není načteno, přeskakuji událost: ${eventName}`);
      return;
    }

    // Validace názvu události
    if (!eventName || typeof eventName !== 'string') {
      console.error('[GA4] Neplatný název události:', eventName);
      return;
    }

    // Kontrola, že parametry neobsahují citlivé údaje
    // (Pro production byste měli implementovat filtrování PII dat)
    const sanitizedParams = { ...params };

    debugLog('trackEvent', { eventName, params: sanitizedParams });

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, sanitizedParams);
    }

  } catch (error) {
    console.error('[GA4] Chyba při trackování události:', error);
  }
}

/**
 * Helper funkce pro běžné události
 */

// Přihlášení uživatele
export function trackLogin(method = 'jwt') {
  trackEvent('login', { method });
}

// Registrace uživatele
export function trackSignUp(method = 'email') {
  trackEvent('sign_up', { method });
}

// Zahájení testu/výzvy
export function trackStartChallenge(challengeType, difficulty = null) {
  const params = { challenge_type: challengeType };
  if (difficulty) {
    params.difficulty = difficulty;
  }
  trackEvent('start_challenge', params);
}

// Dokončení testu
export function trackCompleteTest(testType, score, durationSeconds = null) {
  const params = {
    test_type: testType,
    score: Math.round(score),
  };
  if (durationSeconds) {
    params.duration_seconds = Math.round(durationSeconds);
  }
  trackEvent('complete_test', params);
}

// Výběr lekce/tématu
export function trackSelectContent(contentType, itemId) {
  trackEvent('select_content', {
    content_type: contentType,
    item_id: itemId,
  });
}

// Hledání
export function trackSearch(searchTerm) {
  trackEvent('search', {
    search_term: searchTerm,
  });
}

// Sdílení
export function trackShare(method, contentType, itemId) {
  trackEvent('share', {
    method,
    content_type: contentType,
    item_id: itemId,
  });
}

/**
 * Export pro TypeScript deklarace
 */
export default {
  initGA,
  isGALoaded,
  trackPageView,
  trackEvent,
  trackLogin,
  trackSignUp,
  trackStartChallenge,
  trackCompleteTest,
  trackSelectContent,
  trackSearch,
  trackShare,
};
